import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

interface SearchableDropdownProps<T> {
  options: T[];
  value: string | null;
  onChange: (id: string | null) => void;
  getLabel: (item: T) => string;
  getId: (item: T) => string;
  getGroup?: (item: T) => string;
  placeholder?: string;
  disabled?: boolean;
  disabledHint?: string;
  size?: 'sm' | 'md';
}

function SearchableDropdown<T>({
  options,
  value,
  onChange,
  getLabel,
  getId,
  getGroup,
  placeholder = 'Sélectionner…',
  disabled = false,
  disabledHint,
  size = 'sm',
}: SearchableDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const selectedItem = useMemo(
    () => (value ? options.find((o) => getId(o) === value) : null),
    [value, options, getId]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => getLabel(o).toLowerCase().includes(q));
  }, [options, search, getLabel]);

  // Group options if getGroup provided
  const grouped = useMemo(() => {
    if (!getGroup) return null;
    const groups = new Map<string, T[]>();
    for (const item of filtered) {
      const g = getGroup(item);
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(item);
    }
    return groups;
  }, [filtered, getGroup]);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setIsOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
    },
    [onChange]
  );

  const isSm = size === 'sm';
  const inputCls = isSm ? 'text-xs py-1 px-2' : 'text-sm py-1.5 px-2.5';
  const optionCls = isSm ? 'text-xs px-2 py-1' : 'text-sm px-2.5 py-1.5';
  const listboxId = `listbox-${useMemo(() => Math.random().toString(36).slice(2, 8), [])}`;

  return (
    <div ref={containerRef} className="relative w-full" role="combobox" aria-expanded={isOpen} aria-haspopup="listbox" aria-controls={listboxId}>
      {/* Trigger / input */}
      <div
        className={`flex items-center border rounded-md bg-white cursor-pointer transition-colors ${
          disabled
            ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
            : isOpen
            ? 'border-violet-400 ring-1 ring-violet-200'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        title={disabled && disabledHint ? disabledHint : undefined}
        onClick={() => {
          if (!disabled) setIsOpen(true);
        }}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={selectedItem ? getLabel(selectedItem) : placeholder}
            className={`flex-1 min-w-0 outline-none bg-transparent ${inputCls}`}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-label={placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
                setSearch('');
              }
            }}
          />
        ) : (
          <span
            className={`flex-1 min-w-0 truncate ${inputCls} ${
              selectedItem ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            {selectedItem ? getLabel(selectedItem) : placeholder}
          </span>
        )}

        {value && !disabled ? (
          <button
            onClick={handleClear}
            className="p-0.5 mr-1 rounded hover:bg-gray-100"
            aria-label="Effacer la sélection"
          >
            <XMarkIcon className="w-3 h-3 text-gray-400" />
          </button>
        ) : (
          <ChevronUpDownIcon className={`w-3.5 h-3.5 text-gray-400 mr-1 flex-shrink-0`} aria-hidden="true" />
        )}
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <div id={listboxId} role="listbox" className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {filtered.length === 0 ? (
            <div className={`${optionCls} text-gray-400 italic`}>Aucun résultat</div>
          ) : grouped ? (
            Array.from(grouped.entries()).map(([group, items]) => (
              <div key={group} role="group" aria-label={group}>
                <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 sticky top-0" aria-hidden="true">
                  {group}
                </div>
                {items.map((item) => {
                  const id = getId(item);
                  return (
                    <div
                      key={id}
                      role="option"
                      aria-selected={id === value}
                      className={`${optionCls} cursor-pointer hover:bg-violet-50 ${
                        id === value ? 'bg-violet-50 text-violet-700 font-medium' : 'text-gray-700'
                      }`}
                      onClick={() => handleSelect(id)}
                    >
                      {getLabel(item)}
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            filtered.map((item) => {
              const id = getId(item);
              return (
                <div
                  key={id}
                  role="option"
                  aria-selected={id === value}
                  className={`${optionCls} cursor-pointer hover:bg-violet-50 ${
                    id === value ? 'bg-violet-50 text-violet-700 font-medium' : 'text-gray-700'
                  }`}
                  onClick={() => handleSelect(id)}
                >
                  {getLabel(item)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableDropdown;
